/**
 * The estimated curve compared with the measured one.
 *
 * A single R² says whether the result is good or bad; it does not say **where**
 * it goes wrong. On a log curve "where" is nearly always the more important
 * question: a model that is good on average but falls apart precisely over the
 * reservoir interval is useless, while an unremarkable one that
 * holds steady there can actually be used.
 *
 * So there are three pictures rather than one number:
 *
 * * **The depth trace** — both curves on one axis. This is where you see
 *   whether the error is spread evenly or piled into one bed.
 * * **The crossplot** with a one-to-one line. A slope off that line means the
 *   model is compressing the range: the classic symptom of a regression pulling
 *   segalanya ke arah rerata.
 * * **The residual distribution**. A mean away from zero means a fixed bias —
 *   bias tetap jauh lebih mudah diperbaiki daripada hamburan.
 */
import { useMemo } from "react";
import type { LogCompare } from "../types";

const TRACK_W = 260;
const TRACK_H = 460;
const PLOT = 300;

function extent(values: (number | null)[]): [number, number] {
  const finite = values.filter((v): v is number => v !== null && Number.isFinite(v));
  if (!finite.length) return [0, 1];
  const lo = Math.min(...finite);
  const hi = Math.max(...finite);
  return lo === hi ? [lo - 1, hi + 1] : [lo, hi];
}

export function EstimateComparison({ data }: { data: LogCompare }) {
  const { track, scatter, residual, metrics } = data;

  const [vLo, vHi] = useMemo(() => {
    // One scale for both curves. Separate scales would make an estimate that
    // compresses the range look like a perfect match to the measured curve.
    const [aLo, aHi] = extent(track.measured);
    const [bLo, bHi] = extent(track.estimated);
    return [Math.min(aLo, bLo), Math.max(aHi, bHi)];
  }, [track]);

  const [dLo, dHi] = useMemo(() => extent(track.md), [track]);

  const path = (values: (number | null)[]) => {
    const parts: string[] = [];
    let open = false;
    values.forEach((v, i) => {
      const d = track.md[i];
      if (v === null || d === null) {
        open = false;
        return;
      }
      const x = ((v - vLo) / (vHi - vLo || 1)) * TRACK_W;
      const y = ((d - dLo) / (dHi - dLo || 1)) * TRACK_H;
      parts.push(`${open ? "L" : "M"}${x.toFixed(1)},${y.toFixed(1)}`);
      open = true;
    });
    return parts.join(" ");
  };

  const maxCount = Math.max(1, ...residual.counts);
  const rLo = residual.edges[0];
  const rHi = residual.edges[residual.edges.length - 1];

  const label = (value: number | null | undefined, digits = 3) =>
    value === null || value === undefined || !Number.isFinite(value)
      ? "—"
      : value.toFixed(digits);

  const box: React.CSSProperties = {
    background: "var(--canvas)",
    border: "1px solid var(--line)",
    borderRadius: 6,
  };
  const caption: React.CSSProperties = {
    font: "600 9.5px var(--font-mono)",
    color: "var(--ink-5)",
    marginBottom: 4,
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
        <span style={{ font: "600 11.5px var(--font-sans)", color: "var(--ink)" }}>
          {data.target} taksiran lawan terukur — {data.well}
        </span>
        <span style={{ font: "400 10px var(--font-mono)", color: "var(--ink-5)" }}>
          {data.task === "classification"
            ? `akurasi ${label(metrics.accuracy)} · F1 makro ${label(metrics.f1_macro)}`
            : `R² ${label(metrics.r2)} · RMSE ${label(metrics.rmse, 2)} · MAE ${label(metrics.mae, 2)}`}
          {" · "}
          {scatter.paired.toLocaleString("en-US")} paired samples
        </span>
      </div>

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-start" }}>
        <div>
          <div style={caption}>DEPTH TRACK</div>
          <svg viewBox={`0 0 ${TRACK_W} ${TRACK_H}`} style={{ ...box, width: TRACK_W }}>
            <path d={path(track.measured)} fill="none" stroke="var(--ink-2)" strokeWidth={1.1} />
            <path
              d={path(track.estimated)}
              fill="none"
              stroke="var(--accent)"
              strokeWidth={1.1}
              strokeDasharray="4 3"
            />
          </svg>
          <div style={{ display: "flex", gap: 10, marginTop: 4, font: "400 9px var(--font-mono)" }}>
            <span style={{ color: "var(--ink-3)" }}>— measured</span>
            <span style={{ color: "var(--accent)" }}>-- estimated</span>
            <span style={{ color: "var(--ink-6)", marginLeft: "auto" }}>
              {dLo.toFixed(0)}–{dHi.toFixed(0)} m
            </span>
          </div>
        </div>

        <div>
          <div style={caption}>CROSSPLOT — MEASURED AGAINST ESTIMATED</div>
          <svg viewBox={`0 0 ${PLOT} ${PLOT}`} style={{ ...box, width: PLOT }}>
            {/* The one-to-one line. A cloud flatter than the line
                ini berarti model menekan rentang. */}
            <line x1={0} y1={PLOT} x2={PLOT} y2={0} stroke="var(--line-strong)"
                  strokeWidth={1} strokeDasharray="5 4" />
            {scatter.measured.map((m, i) => {
              const e = scatter.estimated[i];
              if (m === null || e === null) return null;
              const x = ((m - vLo) / (vHi - vLo || 1)) * PLOT;
              const y = PLOT - ((e - vLo) / (vHi - vLo || 1)) * PLOT;
              return <circle key={i} cx={x} cy={y} r={0.9} fill="var(--accent)" fillOpacity={0.25} />;
            })}
          </svg>
          <div style={{ marginTop: 4, font: "400 9px var(--font-mono)", color: "var(--ink-6)" }}>
            {scatter.shown.toLocaleString()} of{" "}
            {scatter.paired.toLocaleString("en-US")} points drawn
          </div>
        </div>

        <div>
          <div style={caption}>RESIDUAL SPREAD (ESTIMATED − MEASURED)</div>
          <svg viewBox={`0 0 ${PLOT} 200`} style={{ ...box, width: PLOT }}>
            {residual.counts.map((c, i) => {
              const w = PLOT / residual.counts.length;
              const h = (c / maxCount) * 176;
              return (
                <rect key={i} x={i * w} y={200 - h - 12} width={w + 0.4} height={h}
                      fill="var(--accent)" fillOpacity={0.55} />
              );
            })}
            {/* Nol digambar supaya bias tetap langsung terlihat. */}
            {rLo < 0 && rHi > 0 && (
              <line
                x1={((0 - rLo) / (rHi - rLo)) * PLOT}
                x2={((0 - rLo) / (rHi - rLo)) * PLOT}
                y1={0}
                y2={188}
                stroke="var(--ink-4)"
                strokeWidth={1}
              />
            )}
          </svg>
          <div style={{ marginTop: 4, font: "400 9px var(--font-mono)", color: "var(--ink-6)" }}>
            mean {label(residual.mean, 2)} · sd {label(residual.std, 2)}
            {Math.abs(residual.mean) > residual.std * 0.25 && (
              <span style={{ color: "var(--warn-ink)" }}> · bias stays visible</span>
            )}
          </div>
        </div>
      </div>

      <span style={{ font: "400 9.5px var(--font-sans)", color: "var(--ink-6)", lineHeight: 1.6 }}>
        {data.note}
      </span>
    </div>
  );
}
